import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Award, ShoppingBag, Calendar, FileText, Gift, User, Edit2, ChevronRight, Upload, Camera, Trash2, Image, ClipboardList, X, Eye, Receipt, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, updateUser, isAuthenticated } = useAuth();
  const fileInputRef = useRef(null);
  const ticketInputRef = useRef(null);
  
  // Get default tab from URL params
  const defaultTab = searchParams.get('tab') || 'orders';
  
  const [orders, setOrders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [uploadingPrescription, setUploadingPrescription] = useState(false);
  const [uploadingTicket, setUploadingTicket] = useState(false);
  const [prescriptionNotes, setPrescriptionNotes] = useState('');
  const [ticketAmount, setTicketAmount] = useState('');
  const [ticketNotes, setTicketNotes] = useState('');
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    weight: '',
    height: '',
    allergies: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || '',
        weight: user.weight || '',
        height: user.height || '',
        allergies: user.allergies || ''
      });
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [ordersRes, appointmentsRes, consultationsRes, pointsRes, prescriptionsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/consultations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/points/history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/prescriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tickets/my-tickets`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setOrders(ordersRes.data);
      setAppointments(appointmentsRes.data);
      setConsultations(consultationsRes.data);
      setPointsHistory(pointsRes.data);
      setPrescriptions(prescriptionsRes.data);
      setMyTickets(ticketsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await updateUser({
        name: profileData.name,
        phone: profileData.phone || null,
        weight: profileData.weight ? parseFloat(profileData.weight) : null,
        height: profileData.height ? parseFloat(profileData.height) : null,
        allergies: profileData.allergies || null
      });
      toast.success('Perfil actualizado');
      setEditMode(false);
    } catch (error) {
      toast.error('Error al actualizar perfil');
    }
  };

  const handleRedeemConsultation = async () => {
    try {
      await axios.post(`${API}/points/redeem-consultation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Consulta gratis canjeada! Usa tu voucher al agendar cita.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al canjear');
    }
  };

  // Handle prescription file upload
  const handlePrescriptionUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor sube una imagen de tu receta');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingPrescription(true);

    try {
      // Convert to base64 for storage (in production, use cloud storage)
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        try {
          await axios.post(`${API}/prescriptions`, {
            image_url: base64Image,
            notes: prescriptionNotes
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success('Receta subida correctamente');
          setPrescriptionNotes('');
          fetchData();
        } catch (error) {
          toast.error('Error al subir la receta');
        } finally {
          setUploadingPrescription(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Error al procesar la imagen');
      setUploadingPrescription(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeletePrescription = async (prescriptionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta receta?')) return;
    
    try {
      await axios.delete(`${API}/prescriptions/${prescriptionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receta eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar la receta');
    }
  };

  const [viewingPrescription, setViewingPrescription] = useState(null);
  const [analyzingTicket, setAnalyzingTicket] = useState(false);
  const [ticketPreview, setTicketPreview] = useState(null);

  // Handle ticket image selection and OCR analysis
  const handleTicketImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor sube una imagen del ticket');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setAnalyzingTicket(true);
    toast.info('Analizando ticket con IA...');

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        setTicketPreview(base64Image);
        
        try {
          // Call OCR endpoint
          const ocrRes = await axios.post(`${API}/tickets/analyze`, {
            ticket_image: base64Image
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (ocrRes.data.success && ocrRes.data.total) {
            setTicketAmount(ocrRes.data.total.toString());
            toast.success(`¡Monto detectado: $${ocrRes.data.total.toFixed(2)} MXN!`);
            if (ocrRes.data.date) {
              setTicketNotes(`Fecha: ${ocrRes.data.date}${ocrRes.data.ticket_number ? ` - Ticket #${ocrRes.data.ticket_number}` : ''}`);
            }
          } else {
            toast.warning('No se pudo leer el monto. Por favor ingrésalo manualmente.');
          }
        } catch (error) {
          console.error('OCR error:', error);
          toast.warning('Error al analizar. Ingresa el monto manualmente.');
        } finally {
          setAnalyzingTicket(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Error al procesar la imagen');
      setAnalyzingTicket(false);
    }
  };

  // Handle ticket final submission
  const handleTicketSubmit = async () => {
    if (!ticketPreview) {
      toast.error('Por favor sube una imagen del ticket');
      return;
    }

    if (!ticketAmount || parseFloat(ticketAmount) <= 0) {
      toast.error('Por favor ingresa el monto del ticket');
      return;
    }

    setUploadingTicket(true);

    try {
      const res = await axios.post(`${API}/tickets/submit`, {
        ticket_image: ticketPreview,
        amount: parseFloat(ticketAmount),
        notes: ticketNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`¡Ticket enviado! Ganarás ${res.data.points_to_add} puntos cuando sea aprobado.`);
      setTicketAmount('');
      setTicketNotes('');
      setTicketPreview(null);
      fetchData();
    } catch (error) {
      toast.error('Error al enviar el ticket');
    } finally {
      setUploadingTicket(false);
    }

    if (ticketInputRef.current) {
      ticketInputRef.current.value = '';
    }
  };

  // Legacy handler (kept for compatibility)
  const handleTicketUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ticketAmount || parseFloat(ticketAmount) <= 0) {
      toast.error('Por favor ingresa el monto del ticket');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor sube una imagen del ticket');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingTicket(true);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        
        try {
          const res = await axios.post(`${API}/tickets/submit`, {
            ticket_image: base64Image,
            amount: parseFloat(ticketAmount),
            notes: ticketNotes
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success(`Ticket enviado. Ganarás ${res.data.points_to_add} puntos cuando sea aprobado.`);
          setTicketAmount('');
          setTicketNotes('');
          fetchData();
        } catch (error) {
          toast.error('Error al enviar el ticket');
        } finally {
          setUploadingTicket(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Error al procesar la imagen');
      setUploadingTicket(false);
    }

    if (ticketInputRef.current) {
      ticketInputRef.current.value = '';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      paid: 'bg-blue-100 text-blue-700',
      preparing: 'bg-purple-100 text-purple-700',
      shipped: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-green-100 text-green-700',
      confirmed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pendiente',
      paid: 'Pagado',
      preparing: 'Preparando',
      shipped: 'Enviado',
      delivered: 'Entregado',
      completed: 'Completada',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada'
    };
    return texts[status] || status;
  };

  // Cancel a pending order
  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('¿Estás seguro de cancelar este pedido?')) return;
    
    try {
      await axios.delete(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pedido cancelado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar pedido');
    }
  };

  // Remove an item from a pending order
  const handleRemoveItemFromOrder = async (orderId, productId, currentItems) => {
    if (!window.confirm('¿Quitar este producto del pedido?')) return;
    
    // Filter out the item to remove
    const updatedItems = currentItems
      .filter(item => item.product_id !== productId)
      .map(item => ({
        product_id: item.product_id,
        quantity: item.quantity
      }));
    
    try {
      const res = await axios.put(`${API}/orders/${orderId}/items`, updatedItems, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.status === 'cancelled') {
        toast.info('Pedido cancelado (no quedan productos)');
      } else {
        toast.success('Producto eliminado del pedido');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar pedido');
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container-app">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#2E7DCD] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{user?.name}</h1>
                <p className="text-slate-500">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-[#E52225] rounded-xl text-white">
                <p className="text-2xl font-bold">{user?.points || 0}</p>
                <p className="text-xs">Puntos</p>
              </div>
              <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-profile-btn">
                <Edit2 className="w-4 h-4 mr-2" />
                Editar Perfil
              </Button>
            </div>
          </div>
        </div>

        {/* Points Banner */}
        {user?.points >= 100 && (
          <div className="bg-gradient-to-r from-[#E52225] to-[#C41C1F] rounded-2xl p-6 mb-6 text-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Gift className="w-12 h-12" />
                <div>
                  <h2 className="text-xl font-bold">¡Tienes recompensas disponibles!</h2>
                  <p className="text-red-100">Canjea tus 100 puntos por una consulta gratis o 20% de descuento</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={handleRedeemConsultation}
                  className="bg-white text-[#E52225] hover:bg-red-50"
                  data-testid="redeem-consultation-btn"
                >
                  Consulta Gratis
                </Button>
                <Button 
                  onClick={() => navigate('/productos')}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                >
                  20% Descuento
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl">
            <TabsTrigger value="orders" className="rounded-lg" data-testid="tab-orders">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Pedidos
            </TabsTrigger>
            <TabsTrigger value="appointments" className="rounded-lg" data-testid="tab-appointments">
              <Calendar className="w-4 h-4 mr-2" />
              Citas
            </TabsTrigger>
            <TabsTrigger value="consultations" className="rounded-lg" data-testid="tab-consultations">
              <FileText className="w-4 h-4 mr-2" />
              Consultas
            </TabsTrigger>
            <TabsTrigger value="points" className="rounded-lg" data-testid="tab-points">
              <Award className="w-4 h-4 mr-2" />
              Puntos
            </TabsTrigger>
            <TabsTrigger value="tickets" className="rounded-lg" data-testid="tab-tickets">
              <Receipt className="w-4 h-4 mr-2" />
              Escanear Ticket
            </TabsTrigger>
            <TabsTrigger value="prescriptions" className="rounded-lg" data-testid="tab-prescriptions">
              <ClipboardList className="w-4 h-4 mr-2" />
              Recetas
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {orders.length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin pedidos</h3>
                <p className="text-slate-500 mb-4">Aún no has realizado ningún pedido</p>
                <Button onClick={() => navigate('/productos')} className="btn-primary">
                  Ver Productos
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="card-dashboard" data-testid={`order-${order.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-slate-500">Pedido #{order.id.slice(0, 8)}</p>
                        <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleDateString('es-MX')}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </div>
                    
                    {/* Order items with edit capability for pending orders */}
                    <div className="space-y-2 mb-4">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center text-xs font-medium">
                              {item.quantity}x
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{item.product_name}</p>
                              <p className="text-xs text-slate-500">${item.price?.toFixed(2)} c/u</p>
                            </div>
                          </div>
                          {order.status === 'pending' && order.items.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItemFromOrder(order.id, item.product_id, order.items)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-item-${item.product_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <p className="text-sm text-slate-500">
                          {order.delivery_type === 'pickup' ? 'Recoger en tienda' : 'Envío a domicilio'}
                          {order.delivery_type === 'pickup' && order.status === 'pending' && (
                            <span className="text-blue-600 ml-1">(Listo en ~1 hora)</span>
                          )}
                        </p>
                        <p className="font-bold text-lg text-slate-900">${order.total.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {order.points_earned > 0 && (
                          <p className="text-sm text-green-600">+{order.points_earned} pts</p>
                        )}
                        {order.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelOrder(order.id)}
                            className="text-red-500 border-red-200 hover:bg-red-50"
                            data-testid={`cancel-order-${order.id}`}
                          >
                            Cancelar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            {appointments.length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin citas</h3>
                <p className="text-slate-500 mb-4">No tienes citas programadas</p>
                <Button onClick={() => navigate('/citas')} className="btn-secondary">
                  Agendar Cita
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((appt) => (
                  <div key={appt.id} className="card-dashboard" data-testid={`appointment-${appt.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{appt.doctor_name}</h3>
                        <p className="text-sm text-[#2E7DCD]">{appt.specialty}</p>
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <span>{appt.date}</span>
                          <span>{appt.time}</span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(appt.status)}`}>
                        {getStatusText(appt.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Consultations Tab */}
          <TabsContent value="consultations">
            {consultations.length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin consultas</h3>
                <p className="text-slate-500">No tienes historial de consultas médicas</p>
              </div>
            ) : (
              <div className="space-y-4">
                {consultations.map((consultation) => (
                  <div key={consultation.id} className="card-dashboard" data-testid={`consultation-${consultation.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-slate-900">{consultation.doctor_name}</h3>
                        <p className="text-xs text-slate-500">{new Date(consultation.created_at).toLocaleDateString('es-MX')}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Diagnóstico</p>
                        <p className="text-sm text-slate-900">{consultation.diagnosis}</p>
                      </div>
                      {consultation.notes && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Notas</p>
                          <p className="text-sm text-slate-700">{consultation.notes}</p>
                        </div>
                      )}
                      {consultation.prescriptions?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Receta</p>
                          <div className="space-y-2">
                            {consultation.prescriptions.map((rx, i) => (
                              <div key={i} className="bg-slate-50 p-3 rounded-lg">
                                <p className="font-medium text-sm">{rx.medicine_name}</p>
                                <p className="text-xs text-slate-500">{rx.dosage} - {rx.frequency} - {rx.duration}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Points Tab */}
          <TabsContent value="points">
            <div className="card-dashboard mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Tu balance de puntos</p>
                  <p className="text-4xl font-bold text-[#E52225]">{user?.points || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Ganas 10 puntos por cada $110</p>
                  <p className="text-sm text-slate-500">100 puntos = Consulta gratis o 20% desc</p>
                </div>
              </div>
            </div>

            {pointsHistory.length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin movimientos</h3>
                <p className="text-slate-500">Realiza compras para acumular puntos</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pointsHistory.map((tx) => (
                  <div key={tx.id} className="card-dashboard p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{tx.description}</p>
                      <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleDateString('es-MX')}</p>
                    </div>
                    <span className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tickets Tab - Escanear Ticket */}
          <TabsContent value="tickets">
            {/* Upload Ticket Section */}
            <div className="card-dashboard mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Escanear Ticket de Compra</h3>
                  <p className="text-sm text-slate-500">La IA leerá automáticamente el monto de tu ticket</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Step 1: Upload Image */}
                <div className="bg-slate-50 rounded-xl p-4 border-2 border-dashed border-slate-200">
                  <input
                    type="file"
                    ref={ticketInputRef}
                    onChange={handleTicketImageSelect}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {ticketPreview ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <img 
                          src={ticketPreview} 
                          alt="Preview del ticket" 
                          className="w-full max-h-64 object-contain rounded-lg"
                        />
                        <button
                          onClick={() => {
                            setTicketPreview(null);
                            setTicketAmount('');
                            setTicketNotes('');
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {analyzingTicket && (
                        <div className="flex items-center justify-center gap-2 text-blue-600">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm font-medium">Analizando con IA...</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => ticketInputRef.current?.click()}
                      disabled={analyzingTicket}
                      className="w-full py-8 flex flex-col items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      <Camera className="w-10 h-10" />
                      <span className="font-medium">Toca para subir foto del ticket</span>
                      <span className="text-xs">La IA detectará el monto automáticamente</span>
                    </button>
                  )}
                </div>

                {/* Step 2: Confirm Amount (auto-filled by OCR) */}
                {ticketPreview && !analyzingTicket && (
                  <>
                    <div>
                      <Label className="flex items-center gap-2">
                        Monto del Ticket (MXN) *
                        {ticketAmount && <span className="text-xs text-green-600 font-normal">✓ Detectado por IA</span>}
                      </Label>
                      <Input
                        type="number"
                        value={ticketAmount}
                        onChange={(e) => setTicketAmount(e.target.value)}
                        placeholder="Ej: 550"
                        className="mt-1"
                        min="1"
                      />
                      {ticketAmount && parseFloat(ticketAmount) >= 110 && (
                        <p className="text-sm text-green-600 mt-1 font-medium">
                          Ganarás {Math.floor(parseFloat(ticketAmount) / 110) * 10} puntos
                        </p>
                      )}
                      {ticketAmount && parseFloat(ticketAmount) < 110 && (
                        <p className="text-sm text-amber-600 mt-1">
                          Necesitas mínimo $110 para ganar puntos
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label>Notas (opcional)</Label>
                      <Input
                        value={ticketNotes}
                        onChange={(e) => setTicketNotes(e.target.value)}
                        placeholder="Ej: Compra de medicamentos para diabetes"
                        className="mt-1"
                      />
                    </div>
                    
                    <Button
                      onClick={handleTicketSubmit}
                      disabled={uploadingTicket || !ticketAmount || parseFloat(ticketAmount) <= 0}
                      className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg"
                      data-testid="submit-ticket-btn"
                    >
                      {uploadingTicket ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Enviar Ticket
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* My Tickets List */}
            <h3 className="font-semibold text-lg mb-4">Mis Tickets Enviados</h3>
            {myTickets.length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin tickets</h3>
                <p className="text-slate-500">Sube tu primer ticket para empezar a ganar puntos</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myTickets.map((ticket) => (
                  <div key={ticket.id} className="card-dashboard p-4" data-testid={`ticket-${ticket.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                        <img
                          src={ticket.ticket_image}
                          alt="Ticket"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold">${ticket.amount.toFixed(2)} MXN</p>
                            <p className="text-sm text-slate-500">
                              {new Date(ticket.created_at).toLocaleDateString('es-MX')}
                            </p>
                            {ticket.notes && (
                              <p className="text-sm text-slate-600 mt-1">{ticket.notes}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {ticket.status === 'pending' && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                                <Clock className="w-3 h-3" />
                                Pendiente
                              </span>
                            )}
                            {ticket.status === 'approved' && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                <CheckCircle className="w-3 h-3" />
                                +{ticket.points_to_add} pts
                              </span>
                            )}
                            {ticket.status === 'rejected' && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                                <XCircle className="w-3 h-3" />
                                Rechazado
                              </span>
                            )}
                          </div>
                        </div>
                        {ticket.admin_notes && (
                          <p className="text-xs text-slate-500 mt-2 italic">
                            Nota del admin: {ticket.admin_notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Prescriptions Tab */}
          <TabsContent value="prescriptions">
            {/* Upload Section */}
            <div className="card-dashboard mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#2E7DCD] rounded-xl flex items-center justify-center">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Subir Nueva Receta</h3>
                  <p className="text-sm text-slate-500">Sube una foto de tu receta médica</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Notas (opcional)</Label>
                  <Input
                    value={prescriptionNotes}
                    onChange={(e) => setPrescriptionNotes(e.target.value)}
                    placeholder="Ej: Receta para dolor de cabeza"
                    className="mt-1"
                  />
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePrescriptionUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPrescription}
                    className="flex-1 btn-secondary"
                    data-testid="upload-prescription-btn"
                  >
                    {uploadingPrescription ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 mr-2" />
                        Tomar Foto / Seleccionar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Prescriptions List */}
            {prescriptions.length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin recetas</h3>
                <p className="text-slate-500">Sube tus recetas médicas para tenerlas siempre a la mano</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {prescriptions.map((prescription) => (
                  <div key={prescription.id} className="card-dashboard p-4" data-testid={`prescription-${prescription.id}`}>
                    <div className="relative aspect-[4/3] bg-slate-100 rounded-lg mb-3 overflow-hidden">
                      <img
                        src={prescription.image_url}
                        alt="Receta médica"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setViewingPrescription(prescription)}
                          className="mr-2"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeletePrescription(prescription.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">
                          {new Date(prescription.created_at).toLocaleDateString('es-MX')}
                        </p>
                        {prescription.notes && (
                          <p className="text-sm text-slate-700 truncate">{prescription.notes}</p>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        prescription.status === 'verified' ? 'bg-green-100 text-green-700' :
                        prescription.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {prescription.status === 'verified' ? 'Verificada' :
                         prescription.status === 'rejected' ? 'Rechazada' : 'Pendiente'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* View Prescription Dialog */}
        <Dialog open={!!viewingPrescription} onOpenChange={() => setViewingPrescription(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Ver Receta</DialogTitle>
            </DialogHeader>
            {viewingPrescription && (
              <div className="space-y-4">
                <img
                  src={viewingPrescription.image_url}
                  alt="Receta médica"
                  className="w-full rounded-lg"
                />
                {viewingPrescription.notes && (
                  <p className="text-slate-700">
                    <strong>Notas:</strong> {viewingPrescription.notes}
                  </p>
                )}
                <p className="text-sm text-slate-500">
                  Subida el {new Date(viewingPrescription.created_at).toLocaleDateString('es-MX')}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Profile Dialog */}
        <Dialog open={editMode} onOpenChange={setEditMode}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Perfil</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Peso (kg)</Label>
                  <Input
                    type="number"
                    value={profileData.weight}
                    onChange={(e) => setProfileData({ ...profileData, weight: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Altura (cm)</Label>
                  <Input
                    type="number"
                    value={profileData.height}
                    onChange={(e) => setProfileData({ ...profileData, height: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Alergias</Label>
                <Input
                  value={profileData.allergies}
                  onChange={(e) => setProfileData({ ...profileData, allergies: e.target.value })}
                  placeholder="Ninguna"
                  className="mt-1"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditMode(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleUpdateProfile} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Dashboard;
